import React, { useState } from 'react';
import { Upload, Play, LogOut, Calendar, Clock, ClipboardList, Shuffle, ArrowLeftRight, Pencil, Download, DatabaseBackup, ListOrdered } from 'lucide-react';
import { colors } from '../../constants/theme';
import { authService, backupService } from '../../services/api';
import FileUpload from './FileUpload';
import MatchManager from './MatchManager';
import MatchScheduler from './MatchScheduler';
import MatchQueue from './MatchQueue';
import RegistrationsManager from './RegistrationsManager';
import GroupsSimulator from './GroupsSimulator';
import GroupEditor from './GroupEditor';
import ResultsEditor from './ResultsEditor';
import StandingsView from './StandingsView';
import BackupManager from './BackupManager';

const OrganizerDashboard = ({ matches, onLogout, onUpdateScore, onFinishMatch, onMatchesUpdate }) => {
  const [currentView, setCurrentView] = useState('registrations');
  const [loading, setLoading] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const filename = await backupService.download();
      alert(`Backup baixado: ${filename}`);
    } catch (error) {
      alert('Erro ao baixar backup: ' + (error.message || 'desconhecido'));
    } finally {
      setBackingUp(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Tem certeza que deseja sair?')) {
      setLoading(true);
      try {
        await authService.logout();
        onLogout();
      } catch (error) {
        console.error('Erro no logout:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const user = authService.getUser();

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="text-white p-4 shadow-lg" style={{ backgroundColor: colors.primary }}>
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Goleiro Raiz Academy - Organizador</h1>
            {user && (
              <p className="text-sm opacity-75">Logado como: {user.username}</p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleBackup}
              disabled={backingUp}
              title="Baixar snapshot do banco (inscrições + atletas) em JSON"
              className="flex items-center space-x-2 bg-white bg-opacity-20 px-4 py-2 rounded hover:bg-opacity-30 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              <span>{backingUp ? 'Gerando...' : 'Backup'}</span>
            </button>
            <button
              onClick={handleLogout}
              disabled={loading}
              className="flex items-center space-x-2 bg-red-600 px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
            >
              <LogOut className="h-4 w-4" />
              <span>{loading ? 'Saindo...' : 'Sair'}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto p-6">
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setCurrentView('registrations')}
            className={`px-4 py-2 rounded flex items-center space-x-2 transition-colors ${
              currentView === 'registrations' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            style={{ backgroundColor: currentView === 'registrations' ? colors.primary : 'white' }}
          >
            <ClipboardList className="h-4 w-4" />
            <span>Inscrições</span>
          </button>

          <button
            onClick={() => setCurrentView('simulator')}
            className={`px-4 py-2 rounded flex items-center space-x-2 transition-colors ${
              currentView === 'simulator' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            style={{ backgroundColor: currentView === 'simulator' ? colors.primary : 'white' }}
          >
            <Shuffle className="h-4 w-4" />
            <span>Simular grupos</span>
          </button>

          <button
            onClick={() => setCurrentView('groupEditor')}
            className={`px-4 py-2 rounded flex items-center space-x-2 transition-colors ${
              currentView === 'groupEditor' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            style={{ backgroundColor: currentView === 'groupEditor' ? colors.primary : 'white' }}
          >
            <ArrowLeftRight className="h-4 w-4" />
            <span>Editar grupos</span>
          </button>

          <button
            onClick={() => setCurrentView('standings')}
            className={`px-4 py-2 rounded flex items-center space-x-2 transition-colors ${
              currentView === 'standings' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            style={{ backgroundColor: currentView === 'standings' ? colors.primary : 'white' }}
          >
            <ListOrdered className="h-4 w-4" />
            <span>Classificação</span>
          </button>

          <button
            onClick={() => setCurrentView('results')}
            className={`px-4 py-2 rounded flex items-center space-x-2 transition-colors ${
              currentView === 'results' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            style={{ backgroundColor: currentView === 'results' ? colors.primary : 'white' }}
          >
            <Pencil className="h-4 w-4" />
            <span>Editar resultados</span>
          </button>

          <button
            onClick={() => setCurrentView('upload')}
            className={`px-4 py-2 rounded flex items-center space-x-2 transition-colors ${
              currentView === 'upload' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            style={{ backgroundColor: currentView === 'upload' ? colors.primary : 'white' }}
          >
            <Upload className="h-4 w-4" />
            <span>Atletas / CSV</span>
          </button>

          <button
            onClick={() => setCurrentView('scheduler')}
            className={`px-4 py-2 rounded flex items-center space-x-2 transition-colors ${
              currentView === 'scheduler' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            style={{ backgroundColor: currentView === 'scheduler' ? colors.primary : 'white' }}
          >
            <Calendar className="h-4 w-4" />
            <span>Iniciar Partidas</span>
          </button>

          <button
            onClick={() => setCurrentView('queue')}
            className={`px-4 py-2 rounded flex items-center space-x-2 transition-colors ${
              currentView === 'queue' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            style={{ backgroundColor: currentView === 'queue' ? colors.primary : 'white' }}
          >
            <Clock className="h-4 w-4" />
            <span>Fila de Partidas</span>
          </button>
          
          <button
            onClick={() => setCurrentView('matches')}
            className={`px-4 py-2 rounded flex items-center space-x-2 transition-colors ${
              currentView === 'matches' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            style={{ backgroundColor: currentView === 'matches' ? colors.primary : 'white' }}
          >
            <Play className="h-4 w-4" />
            <span>Partidas Ativas ({matches.length})</span>
          </button>

          <button
            onClick={() => setCurrentView('backup')}
            className={`px-4 py-2 rounded flex items-center space-x-2 transition-colors ${
              currentView === 'backup' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            style={{ backgroundColor: currentView === 'backup' ? colors.primary : 'white' }}
          >
            <DatabaseBackup className="h-4 w-4" />
            <span>Backup</span>
          </button>
        </div>

        {currentView === 'registrations' && <RegistrationsManager />}

        {currentView === 'simulator' && <GroupsSimulator />}

        {currentView === 'groupEditor' && <GroupEditor />}

        {currentView === 'standings' && <StandingsView />}

        {currentView === 'results' && <ResultsEditor />}

        {currentView === 'upload' && <FileUpload />}
        
        {currentView === 'scheduler' && (
          <MatchScheduler onMatchesUpdate={onMatchesUpdate} />
        )}

        {currentView === 'queue' && (
          <MatchQueue onQueueUpdate={onMatchesUpdate} />
        )}
        
        {currentView === 'matches' && (
          <MatchManager
            matches={matches}
            onUpdateScore={onUpdateScore}
            onFinishMatch={onFinishMatch}
            onMatchesUpdate={onMatchesUpdate}
          />
        )}

        {currentView === 'backup' && (
          <BackupManager onRestored={onMatchesUpdate} />
        )}
      </div>
    </div>
  );
};

export default OrganizerDashboard;